'use client';

import type { FormControlProps, FormHelperTextSlotProps } from '@magicschool/spellbook/components/Form/slots';
import {
  FORM_CONTROL_SLOTS,
  FormCheckboxSlot,
  FormErrorMessageSlot,
  FormFooterSlot,
  FormFooterTextSlot,
  FormHelperTextSlot,
  FormLabelSlot,
  FormTooltipSlot,
} from '@magicschool/spellbook/components/Form/slots';
import type { SlotComponent } from '@magicschool/spellbook/components/Slot';
import { Slot } from '@magicschool/spellbook/components/Slot';
import { useId } from '@magicschool/spellbook/hooks/useId';
import { useSlots } from '@magicschool/spellbook/hooks/useSlots';
import { flattenChildren } from '@magicschool/spellbook/lib/slots';
import { cn, mergeIds } from '@magicschool/spellbook/lib/utils';
import type { ComponentProps, ComponentType, PropsWithChildren, ReactElement, ReactNode } from 'react';
import { cloneElement, isValidElement, useEffect } from 'react';

/** Lines below-control text up with a Checkbox's label rather than its box. */
const CHECKBOX_LABEL_INDENT = 'ml-7.5';

type ControlComponent = ComponentType<never> & { displayName: string; isGroup: boolean };

/** Every recognised `Form.*` control element among the children, in order. */
function findControls(children: ReactNode) {
  return flattenChildren(children).filter(
    (child): child is ReactElement<FormControlProps> => isValidElement(child) && FORM_CONTROL_SLOTS.has(child.type as ComponentType<never>),
  );
}

type SlotElement = ReactElement | ReactElement[] | undefined;

function firstElement<TProps>(slot: SlotElement) {
  return (Array.isArray(slot) ? slot[0] : slot) as ReactElement<TProps> | undefined;
}

function lastElement<TProps>(slot: SlotElement) {
  return (Array.isArray(slot) ? slot.at(-1) : slot) as ReactElement<TProps> | undefined;
}

function hasContent(slot: SlotElement) {
  return Boolean(firstElement<PropsWithChildren>(slot)?.props.children);
}

function hasFooterText(footerSlot: SlotElement) {
  const footer = firstElement<PropsWithChildren>(footerSlot);
  if (!footer) return false;

  const texts = flattenChildren(footer.props.children).filter(
    (child): child is ReactElement => isValidElement(child) && child.type === FormFooterTextSlot,
  );
  if (texts.length > 1) {
    throw new Error('Form.Footer can only contain one Form.Footer.Text');
  }
  return hasContent(texts[0]);
}

function useDevWarnings(controls: ReactElement[], rest: ReactNode[]) {
  // Extra controls of another type also land in `rest`; count them once, under the controls message.
  const strayChildren = rest.length - Math.max(controls.length - 1, 0);
  const problems =
    process.env.NODE_ENV === 'production'
      ? ''
      : [
          controls.length === 0 &&
            'No Form.* control found among its direct children (a control inside a wrapper element is not detected).',
          controls.length > 1 &&
            `Found ${controls.length} controls (${controls.map((c) => (c.type as unknown as ControlComponent).displayName).join(', ')}); rendering only the last.`,
          strayChildren > 0 && `Not rendering ${strayChildren} child(ren) that are not Form.* slots.`,
        ]
          .filter(Boolean)
          .join(' ');

  useEffect(() => {
    if (problems) {
      console.warn(`[Form.Group] ${problems} Each Form.Group takes one Form.* control plus Form.* slots.`);
    }
  }, [problems]);
}

type UseFormGroupSlotsParams = {
  children: ReactNode;
  controlId?: string;
  name?: string;
  required: boolean;
  disabled: boolean;
  invalid: boolean;
};

function useFormGroupSlots({ children, controlId, name, required, disabled, invalid }: UseFormGroupSlotsParams) {
  const controls = findControls(children);
  const controlElement = controls.at(-1);
  const control = controlElement?.type as ControlComponent | undefined;
  // A state set on the control itself counts too, so the label and describedby stay in sync with it.
  const controlProps: FormControlProps = controlElement?.props ?? {};
  const isRequired = required || Boolean(controlProps.required);
  const isInvalid = invalid || Boolean(controlProps.invalid);

  const generatedId = useId();
  const id = controlProps.id ?? controlId ?? generatedId;
  // `-form-label`, because Checkbox and Switch already give their own label `${id}-label`.
  const labelId = `${id}-form-label`;
  const helperTextId = `${id}-helper-text`;
  const errorMessageId = `${id}-error-message`;
  const footerTextId = `${id}-footer-text`;
  const { slots, rest } = useSlots(children, {
    // A group control's root is a `<div>`, which `<label for>` cannot target; it is named through `labelId` instead.
    label: { Component: FormLabelSlot, props: { id: labelId, htmlFor: control?.isGroup ? undefined : id, required: isRequired } },
    tooltip: { Component: FormTooltipSlot },
    ...(control && {
      control: {
        Component: control as unknown as SlotComponent<unknown>,
        props: { id, name, required, disabled, invalid, labelId },
      },
    }),
    helperText: { Component: FormHelperTextSlot, props: { id: helperTextId } },
    errorMessage: { Component: FormErrorMessageSlot, props: { id: errorMessageId } },
    footer: { Component: FormFooterSlot, props: { footerTextId } },
  });

  useDevWarnings(controls, rest);

  const position = firstElement<FormHelperTextSlotProps>(slots.helperText)?.props.position;
  const helperTextAbove = position ? position === 'above-control' : Boolean(control?.isGroup);
  const hasHelperText = hasContent(slots.helperText);
  const showError = hasContent(slots.errorMessage);

  const aboveControl = hasHelperText && helperTextAbove ? slots.helperText : undefined;
  // Below the control, an error message or the invalid state takes the helper text's place.
  let belowControl: SlotElement;
  if (showError) {
    belowControl = slots.errorMessage;
  } else if (hasHelperText && !helperTextAbove && !isInvalid) {
    belowControl = slots.helperText;
  }

  // With several controls of one type `useSlots` returns them all; render only the last, as the warning says.
  const controlSlot = lastElement<FormControlProps>(slots.control);
  const describedBy = mergeIds(
    controlSlot?.props['aria-describedby'],
    hasHelperText && (aboveControl || belowControl === slots.helperText) && helperTextId,
    showError && errorMessageId,
    hasFooterText(slots.footer) && footerTextId,
  );

  return {
    slots,
    control: controlSlot && describedBy ? cloneElement(controlSlot, { 'aria-describedby': describedBy }) : controlSlot,
    isCheckbox: control === FormCheckboxSlot,
    aboveControl,
    belowControl,
  };
}

export type FormGroupSlotProps = Omit<ComponentProps<'div'>, 'id'> & {
  /** Fixes the control's `id` instead of generating one. Use when an integration or test needs a stable id */
  controlId?: string;
  /** Passed to the control as its `name` for form submission */
  name?: string;
  /** Marks the control `required` and adds an asterisk to `Form.Label` */
  required?: boolean;
  /** Disables the control */
  disabled?: boolean;
  /** Puts the control in its error state and lets `Form.ErrorMessage` replace the helper text below it */
  invalid?: boolean;
  /** One `Form.*` control plus any of `Form.Label`, `Form.Tooltip`, `Form.HelperText`, `Form.ErrorMessage` and `Form.Footer`, in any order */
  children: ReactNode;
};

export const FormGroupSlot = Slot<FormGroupSlotProps>({
  displayName: 'Form.Group',
  renderDefault: ({ controlId, name, required = false, disabled = false, invalid = false, className, children, ...props }) => {
    const { slots, control, isCheckbox, aboveControl, belowControl } = useFormGroupSlots({
      children,
      controlId,
      name,
      required,
      disabled,
      invalid,
    });

    const hasHeader = slots.label || slots.tooltip;

    return (
      <div data-spellbook-name="form-group" className={cn('flex flex-col w-full', className)} {...props}>
        <div className="flex items-center gap-1">
          {slots.label}
          {slots.tooltip}
        </div>
        {aboveControl && <div className={cn(hasHeader && 'mt-0.25')}>{aboveControl}</div>}
        <div className={cn((hasHeader || aboveControl) && 'mt-2')}>{control}</div>
        {(belowControl || slots.footer) && (
          <div className={cn('flex items-start justify-between gap-4', isCheckbox ? CHECKBOX_LABEL_INDENT : 'mt-2')}>
            <div className="flex-1">{belowControl}</div>
            {slots.footer}
          </div>
        )}
      </div>
    );
  },
});
